import {
  subYears,
  addYears,
  format,
  isBefore,
  isAfter,
  isValid,
  isEqual,
  isSaturday,
  isSunday,
  getDaysInMonth,
  differenceInMilliseconds,
  differenceInMonths,
  differenceInHours,
  differenceInDays,
  differenceInCalendarDays,
  differenceInYears,
  parse,
  sub,
  addHours,
  addDays,
  addMonths,
  startOfHour,
  startOfDay,
  min,
  isSameMonth,
  startOfMonth,
  endOfMonth,
} from 'date-fns';

import {
  omit,
  range,
  isFinite,
} from 'lodash';

import { DATE_OF_INSURANCE_START_MAX_DAYS } from '@/constants/common';

export const MSEC_IN_ONE_MIN = 60000;
export const DATE_FORMAT = 'yyyy-MM-dd';
export const DATEPICKER_LABEL_FORMAT = 'dd-MM-yyyy';
export const LOCAL_DATE_TEMPLATE = {
  day: null,
  month: null,
  year: null,
};

/**
 * Returns {@link LOCAL_DATE_TEMPLATE} type Object without 'day' key
 * @returns {Object}
 */
export const getDayLessDateTemplate = () => Object.assign({}, omit(LOCAL_DATE_TEMPLATE, ['day']));

/**
 * Check if {@link LOCAL_DATE_TEMPLATE} type Object had all keys (e.g. day, month, year) filled
 * @param {Object} dateObj - {@link LOCAL_DATE_TEMPLATE} type object
 * @param {Array} fields
 * @returns {boolean}
 */
export const checkDateObjFilled = (dateObj, fields = ['day', 'month', 'year']) => {
  return !(
    (fields.includes('day') ? (isFinite(+dateObj.day) && +dateObj.day > 0) : true) &&
    (fields.includes('month') ? isFinite(dateObj.month) : true) &&
    (fields.includes('year') ? isFinite(dateObj.year) : true)
  );
};

/**
 * Return array of years raging from `yearFrom` param with `qty` length
 * @param {Number} qty - amount of years in array
 * @param {Number} [yearFrom=utcTodayYear]
 * @param {Boolean} convertItemsToString
 * @returns {number[]} - array with years
 */
export const getRangeOfYears = (qty, yearFrom = new UtcDate().getFullYear(), convertItemsToString = false) => {
  const rangeOfYears = range(yearFrom - qty, yearFrom + 1).reverse();

  if (convertItemsToString) {
    return rangeOfYears.map(year => year.toString());
  }

  return rangeOfYears;
};

/**
 * Is the date before today utc
 * @param dateString - string with date  {@link DATE_FORMAT}
 * @returns {Boolean}
 */
export const isDateBeforeTodayUtc = dateString => {
  const todayUtc = new UtcDate().startOfDay();
  return new UtcDate(dateString).isBefore(todayUtc);
};

/**
 * returns the number of milliseconds since the Unix Epoch without timezone offset
 * @param {String|Number} date
 * @returns {Number} Date obj without timezone offset
 */
const getUtcTimeInMilliseconds = date => {
  const initial = date ? new Date(date) : new Date();
  const userTimezoneOffset = initial.getTimezoneOffset() * MSEC_IN_ONE_MIN;
  return initial.getTime() + userTimezoneOffset;
};

/**
 * Class representing Date with date fns methods.
 * @extends Date
 */
export class ExtDate extends Date {
  // private methods
  /**
   * Checks if a value is instance of Date.
   * @private
   * @param {Date} date
   * @returns {boolean}
   */
  _isDate (date) { return date instanceof Date; }

  /**
   * Returns parsed date or throw an error if no param given
   * @private
   * @param {any} date - value to parse as Date
   * @returns {*|UtcDate}
   */
  _getDate (date) {
    if (!date) {
      throw new Error('argument required');
    }
    return this._isDate(date) ? date : new Date(date);
  };
  // end private methods

  /**
   * Is the date before the date to compare?
   * @param {Date|Number} date - the date to compare with
   * @returns {Boolean} - Is the date before the date to compare?
   */
  isBefore (date) {
    return isBefore(this, this._getDate(date));
  }

  /**
   * Is the date after the date to compare?
   * @param {Date|Number} date - the date to compare with
   * @returns {Boolean} - Is the date after the date to compare
   */
  isAfter (date) {
    return isAfter(this, this._getDate(date));
  }

  /**
   * Are the given dates equal?
   * @param {Date|Number} date - the second date to compare
   * @returns {boolean} - the dates are equal
   */
  isEqual (date) {
    return isEqual(this, this._getDate(date));
  }

  /**
   * Is the given date Saturday?
   * @returns {boolean} - today is Saturday
   */
  isSaturday () {
    return isSaturday(this);
  }

  /**
   * Is the given date Sunday?
   * @returns {boolean} - today is Sunday
   */
  isSunday () {
    return isSunday(this);
  }

  /**
   * Is the date before or equal the date to compare?
   * @param {String|Number|Date} date - the second date to compare
   * @returns {Boolean} - the date before or equal the date to compare
   */
  isBeforeOrEqual (date) {
    return this.isBefore(date) || this.isEqual(date);
  }

  /**
   * Is the date after or equal the date to compare?
   * @param {String|Number|Date} date - the second date to compare
   * @returns {Boolean} - the date after or equal the date to compare
   */
  isAfterOrEqual (date) {
    return this.isAfter(date) || this.isEqual(date);
  }

  /**
   * Add the specified number of days to the given date.
   * @param {Number} amount - the amount of days to be added. Positive decimals will be rounded using Math.floor, decimals less than zero will be rounded using Math.ceil.
   * @returns {ExtDate} - the new date with the days added
   */
  addDays (amount) {
    const milliseconds = addDays(this, amount).getTime();
    this.setTime(milliseconds);
    return this;
  }

  /**
   * Add the specified number of hours to the given date.
   * @param amount - the amount of hours to be added.
   * Positive decimals will be rounded using Math.floor,
   * decimals less than zero will be rounded using Math.ceil.
   * @returns {ExtDate} - the new date with the hours added
   */
  addHours (amount) {
    const milliseconds = addHours(this, amount).getTime();
    this.setTime(milliseconds);
    return this;
  }

  /**
   * Add the specified number of months to the given date.
   * @param {Number} amount - the amount of months to be added. Positive decimals will be rounded using Math.floor, decimals less than zero will be rounded using Math.ceil.
   * @returns {ExtDate} - the new date with the months added
   */
  addMonths (amount) {
    const milliseconds = addMonths(this, amount).getTime();
    this.setTime(milliseconds);
    return this;
  }

  /**
   * Add the specified number of years to the given date.
   * @param {Number} amount - the amount of years to be added. Positive decimals will be rounded using Math.floor, decimals less than zero will be rounded using Math.ceil.
   * @returns {ExtDate} - the new date with the years added
   */
  addYears (amount) {
    const milliseconds = addYears(this, amount).getTime();
    this.setTime(milliseconds);
    return this;
  }

  /**
   * Return the start of an hour for the given date. The result will be in the local timezone.
   * @returns {Date} - the start of an hour
   */
  startOfHour () {
    const milliseconds = startOfHour(this).getTime();
    this.setTime(milliseconds);
    return this;
  }

  /**
   * Return the start of a day for the given date. The result will be in the local timezone.
   * @returns {Date} - the start of a day
   */
  startOfDay () {
    return startOfDay(this);
  }

  /**
   * Subtract the specified years, months, weeks, days, hours, minutes and seconds from the given date.
   * @param {Object} duration - the object with years, months, weeks, days, hours, minutes and seconds to be subtracted
   * @returns {ExtDate} - the new date with the seconds subtracted
   */
  sub (duration) {
    const milliseconds = sub(this, duration).getTime();
    this.setTime(milliseconds);
    return this;
  }

  /**
   * Subtract the specified number of years from the given date.
   * @param {Number} amount - the amount of years to be subtracted. Positive decimals will be rounded using Math.floor, decimals less than zero will be rounded using Math.ceil.
   * @returns {ExtDate} the new date with the years subtracted
   */
  subYears (amount) {
    const milliseconds = subYears(this, amount).getTime();
    this.setTime(milliseconds);
    return this;
  }

  /**
   * Get the number of full years between the given dates.
   * @param {Date|Number} date - the earlier date
   * @returns {Number} the number of full years
   */
  differenceInYears (date) {
    return differenceInYears(this, this._getDate(date));
  }

  /**
   * Returns the earliest of the given dates.
   * @param {Date} date - the date
   * @returns {Date} earlier date
   */
  min (date) {
    return min([this, this._getDate(date)]);
  }

  /**
   * Get the number of full milliseconds between the given dates.
   * @param {String|Number|Date} date - the earlier date
   * @returns {number} - the number of full milliseconds
   */
  differenceInMilliseconds (date) {
    return differenceInMilliseconds(this, this._getDate(date));
  }

  /**
   * Get the number of full months between the given dates.
   * @param {String|Number|Date} date - the earlier date
   * @returns {number} - the number of full months
   */
  differenceInMonths (date) {
    return differenceInMonths(this, this._getDate(date));
  }

  /**
   * Get the number of full hours between the given dates.
   * @param {String|Number|Date} date - the earlier date
   * @returns {number} - the number of full hours
   */
  differenceInHours (date) {
    return differenceInHours(this, this._getDate(date));
  }

  /**
   * Get the number of full days between the given dates.
   * @param {String|Number|Date} date - the earlier date
   * @returns {number} - the number of full days
   */
  differenceInDays (date) {
    return differenceInDays(this, this._getDate(date));
  }

  /**
   * Return the formatted date string in the given format.
   * https://date-fns.org/v2.19.0/docs/format
   * @param {String} pattern - the string of tokens
   * @returns {String} - the formatted date string
   */
  format (pattern = DATE_FORMAT) {
    return format(this, pattern);
  }

  /**
   * Return the date parsed from string using the given format string.
   * https://date-fns.org/v2.19.0/docs/parse
   * @param {String} date - the string to parse
   * @param {String} pattern - the string of tokens
   * @returns {ExtDate} - the parsed date
   */
  parse (date, pattern) {
    const milliseconds = parse(date, pattern, new Date()).getTime();
    this.setTime(milliseconds);
    return this;
  }

  /**
   * Get the number of days in a month of the given date.
   * @returns {Number} - the number of days in a month
   */
  getDaysInMonth () {
    return getDaysInMonth(this);
  }

  /**
   * Returns false if argument is Invalid Date and true otherwise. Argument is converted to Date using toDate. See toDate Invalid Date is a Date, whose time value is NaN.
   * https://date-fns.org/v2.19.0/docs/isValid
   * @returns {boolean} - the date is valid
   */
  isValid () { return isValid(this); }

  /**
   * Get the number of full days between the given dates.
   * @param {Date|Number} date - the earlier date
   * @returns {Number} the number of full days
   */
  differenceInCalendarDays (date) {
    return differenceInCalendarDays(this, this._getDate(date));
  }

  /**
  * Are the given dates in the same month (and year)
  * @param {Date|Number} date
  * @returns {Boolean} the dates are in the same month (and year)
  */
  isSameMonth (date) {
    return isSameMonth(this, this._getDate(date));
  }

  /**
  * Return the start of a month for the given date.
   * @returns {Date} - the start of a month
  */
  startOfMonth () {
    return startOfMonth(this);
  }

  /**
  * Return the end of a month for the given date.
   * @returns {Date} - the end of a month
  */
  endOfMonth () {
    return endOfMonth(this);
  }
}

/**
 * Class representing ExtDate with UTC time.
 * @extends ExtDate
 */
export class UtcDate extends ExtDate {
  constructor (date) {
    super(getUtcTimeInMilliseconds(date));
  }

  /**
   * Returns parsed utc date or throw an error if no param given
   * @private
   * @param {any} date - value to parse as Date
   * @returns {*|UtcDate}
   */
  _getDate (date) {
    if (!date) {
      throw new Error('argument required');
    }
    return this._isDate(date) ? date : new UtcDate(date);
  };
}

/**
 * Get minimal possible Date of insurance start based on UTC time
 * @returns {String} - {@link DATE_FORMAT} - formatted date
 */
export const getDateOfInsuranceMin = () => {
  const today = new ExtDate();
  const todayUtc = new UtcDate();

  // if GTM shift is ahead of UTC date - set min date to today
  return today.isAfter(todayUtc) ? today.format() : todayUtc.format();
};

export const getDateOfInsuranceMax = () => {
  return new UtcDate().addDays(DATE_OF_INSURANCE_START_MAX_DAYS).format();
};

export const getDateOfInsuranceLabel = (dateOfInsurance, dateFormat = DATEPICKER_LABEL_FORMAT) => {
  if (['', null, undefined].includes(dateOfInsurance)) return null;

  const today = new ExtDate();
  const utcDateOfInsurance = new UtcDate(dateOfInsurance);

  let label = utcDateOfInsurance.format(dateFormat);

  if (today.format() === utcDateOfInsurance.format()) {
    label += ' (today)';
  }
  return label;
};
